import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { errors, i18next } from '@yola/ws-sdk';
import errorModule from '../../../src/js/modules/error';
import CriticalErrorDialog from '../../../src/js/modules/error/components/critical-error-dialog';
import errorTypes from '../../../src/js/modules/error/constants/error-types';

const {
  SESSION_EXPIRED,
  NETWORK_OFFLINE,
  SITE_BELONGS_TO_ANOTHER_USER,
  REQUIRED_COOKIES_ARE_DELETED,
} = errorModule.constants;

const getCaptions = () => ({
  sessionExpiredCaptions: {
    title: i18next.t('Session has expired'),
    description: i18next.t('Please reload to continue building'),
    dismiss: i18next.t('Reload'),
  },
  defaultCaptions: {
    title: i18next.t('Something went wrong'),
    description: i18next.t('Please reload the page'),
    dismiss: i18next.t('Reload'),
  },
  siteBelongsToAnotherUserCaptions: {
    title: i18next.t('Site belongs to another user'),
    description: i18next.t(
      'Refresh the page and sign in to continue using the editor. This may happen if you’re logged into multiple accounts in different tabs'
    ),
    dismiss: i18next.t('Refresh'),
  },
  requiredCookiesAreDeletedCaptions: {
    title: i18next.t('Your browser has deleted required cookies'),
    description: i18next.t(
      'Refresh the page and authorise to continue using editor. It can happen because you deleted browsing data with all cookies'
    ),
    dismiss: i18next.t('Refresh'),
  },
});

class ErrorBoundary extends React.Component {
  componentDidCatch(error, errorInfo) {
    errors.operations.catchError(error, errors.types.CRITICAL_ERROR, errorInfo);
  }

  render() {
    const { children, criticalError, networkStatus } = this.props;

    if (criticalError) {
      if (
        criticalError.message.includes(errorTypes.NETWORK_ERROR) &&
        networkStatus === NETWORK_OFFLINE
      ) {
        return children;
      }

      const {
        sessionExpiredCaptions,
        defaultCaptions,
        siteBelongsToAnotherUserCaptions,
        requiredCookiesAreDeletedCaptions,
      } = getCaptions();
      let captions = defaultCaptions;

      if (criticalError.message === SESSION_EXPIRED) {
        captions = sessionExpiredCaptions;
      }
      if (criticalError.message.includes(SITE_BELONGS_TO_ANOTHER_USER)) {
        captions = siteBelongsToAnotherUserCaptions;
      }
      if (criticalError.message.includes(REQUIRED_COOKIES_ARE_DELETED)) {
        captions = requiredCookiesAreDeletedCaptions;
      }
      return <CriticalErrorDialog captions={captions} />;
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  criticalError: PropTypes.instanceOf(Error),
  networkStatus: PropTypes.string,
};

ErrorBoundary.defaultProps = {
  criticalError: null,
  networkStatus: null,
};

const mapStateToProps = (state) => ({
  criticalError: errorModule.selectors.getCriticalError(state),
  networkStatus: errorModule.selectors.getNetworkStatus(state),
});

export default connect(mapStateToProps)(ErrorBoundary);
